import { cn } from '@/lib/utils';
import type { AgingBucket } from '@jobos/shared';

/**
 * Renders the SERVER's aging verdict. It never recomputes overdue from dates
 * (spec D6) — overdue depends on the client's own threshold, which the browser
 * does not hold, and a component doing date maths on `sentAt` is a defect even
 * on the days it happens to agree.
 */

const BUCKET_STYLES: Record<AgingBucket, string> = {
  '0-30': 'bg-gray-100 text-gray-700',
  '31-60': 'bg-amber-100 text-amber-800',
  '61-90': 'bg-orange-100 text-orange-800',
  '90+': 'bg-red-100 text-red-800',
};

export interface AgingBadgeProps {
  bucket: AgingBucket | null;
  daysOutstanding: number | null;
  isOverdue: boolean;
  className?: string;
}

export function AgingBadge({ bucket, daysOutstanding, isOverdue, className }: AgingBadgeProps) {
  // A draft has never been sent, so it is not ageing. An em dash says that
  // more honestly than "0 days".
  if (bucket === null || daysOutstanding === null) {
    return <span className={cn('text-gray-400', className)}>—</span>;
  }

  return (
    <span
      className={cn(
        // items-center, not items-baseline: the overdue marker is an inline-flex
        // of its own, so baseline alignment lined the badge up against THAT
        // box's baseline and left the dot and the day count sitting at
        // different heights. Every part of the badge is the same size, so
        // centring them is also what reads as one chip.
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium',
        BUCKET_STYLES[bucket],
        className,
      )}
      title={
        isOverdue
          ? `${daysOutstanding} days outstanding — past this client's payment terms`
          : `${daysOutstanding} days outstanding`
      }
    >
      <span className="tabular-nums">{daysOutstanding}d</span>
      {/*
        Overdue was a red inset ring, which inverted the emphasis it was meant
        to add: invisible against the already-red 90+ bucket, loud against the
        amber 31-60 one, so a 60-day invoice read as more urgent than a
        306-day one. A dot inherits the bucket's own foreground, so the bucket
        stays the thing carrying severity and this only marks which rows have
        passed the client's terms.

        The threshold is configurable per client, so "overdue" and "in the
        31-60 bucket" remain different facts — they are simply no longer
        fighting for the same visual channel.
      */}
      {isOverdue && (
        <span className="inline-flex items-center gap-1 font-semibold">
          <span aria-hidden className="h-1 w-1 rounded-full bg-current opacity-70" />
          overdue
        </span>
      )}
    </span>
  );
}
